import * as React from "react";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { useAuth } from "../../context/AuthContext";
import { useUserLabels } from "../../hooks/useUserLabels";
import type { VorgangDto } from "../../services/vorgangService";
import { betreffOhneArt, zeigtArtChip } from "../../data/vorgangArt";
import { isOverdueFrist } from "../dashboard/DashboardShared";
import { DataTable, type Column, type DateRangeValue } from "../redesign/table";
import { I } from "../redesign/kvpIcons";
import { REFINED } from "../risikoanalyseStep/tokens";
import { InitialsAvatar } from "../chat/chatUi";
import { PeopleCell } from "../vorgang/shared/PeopleCell";
import { VorgangProgressMini, VorgangStatusPill } from "../vorgang/shared/StatusCell";
import { DateCell } from "../vorgang/shared/DateCells";
import { personenFilterOptionen } from "../vorgang/shared/people";
import { baueVorgangZeilen, type UnifiedRow } from "../vorgang/shared/unifiedRows";
import { vorgangSprungLink } from "../vorgang/v4/sprungLink";
import { FILTER_SPALTEN, LEERE_FILTER, STATUS_OPTIONEN, type FilterWerte } from "./vorgangFilter";
import "../redesign/contactRedesign.css";

/**
 * Die Vorgänge EINES Kontakts als Tabelle — dieselben Spalten wie die
 * Vorgangstabelle, nur ohne „Kontakt" (PO 08.09.2026: „die View soll die
 * Vorgänge als Tabelle auflisten sowie in Vorgangstabelle, allerdings ohne die
 * Spalte Kontakt"). Der Kontakt steht ja über der Tabelle.
 *
 * Zeilenmodell, Datums-Zellen und Filter-Optionen kommen aus `vorgang/shared` —
 * dieselben, die die Vorgangstabelle benutzt. Spaltenfilter gibt es auch hier,
 * sie greifen aber CLIENTSEITIG auf den geladenen Bestand (siehe
 * `vorgangFilter.ts`). Was bewusst FEHLT, weil es an der mandantenweiten Liste
 * hängt und nicht an einer Kontaktakte: serverseitige Sortierung, gespeicherte
 * Ansichten, Infinite Scroll und das Ändern von Terminen in der Zelle. Die
 * Tabelle ist eine Übersicht; gearbeitet wird im Vorgang.
 */

/** Ausgeblendet bzw. eingeklappt wie in der Vorgangstabelle (Werkseinstellung). */
const VERSTECKT: Record<string, boolean> = { Ersteller: false };

interface Props {
    vorgaenge: VorgangDto[];
    /** Schmal eingeklappte Spalten — vom Reiter gehalten, damit alle Tabellen gleich stehen. */
    eingeklappt: string[];
    onToggleSpalte: (spaltenId: string) => void;
    leerText?: string;
    /**
     * Filterwerte des Reiters. Die Tabelle ZEIGT sie nur (Icon + Popover) —
     * gefiltert wird eine Ebene höher, damit alle Abschnitte dieselbe Auswahl
     * benutzen und die Kopfzahlen dazu passen.
     */
    filter?: FilterWerte;
    onFilterChange?: (spaltenId: string, wert: string | boolean | undefined) => void;
    onDatumChange?: (spaltenId: string, wert: DateRangeValue) => void;
    onPersonenChange?: (spaltenId: string, wert: string[]) => void;
}

export const KontaktVorgangTabelle: React.FC<Props> = ({
    vorgaenge,
    eingeklappt,
    onToggleSpalte,
    leerText = "Keine Vorgänge.",
    filter = LEERE_FILTER,
    onFilterChange,
    onDatumChange,
    onPersonenChange,
}) => {
    const navigate = useNavigate();
    const { user, isAdmin, isGfgl, teams } = useAuth();
    const { users } = useUserLabels();
    const [offen, setOffen] = useState<Set<string>>(new Set());

    const rows = useMemo(
        () => baueVorgangZeilen(vorgaenge, { users, currentUserId: user?.id ?? null, isAdmin, isGfgl, teams }),
        [vorgaenge, users, user?.id, isAdmin, isGfgl, teams],
    );
    const personenOptionen = useMemo(() => personenFilterOptionen(users), [users]);

    const umschalten = (key: string) =>
        setOffen((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });

    const columns: Column<UnifiedRow>[] = useMemo(
        () => [
            {
                id: FILTER_SPALTEN.status,
                header: "Status",
                pinned: true,
                filter: { kind: "dropdown", options: STATUS_OPTIONEN },
                width: 170,
                minWidth: 150,
                cell: (row) =>
                    row.kind === "todo" ? (
                        <VorgangStatusPill status={row.status} size="small" />
                    ) : (
                        <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-start", minWidth: 0 }}>
                            <VorgangStatusPill status={row.status} size="small" />
                            <VorgangProgressMini erledigt={row.erledigt} gesamt={row.gesamt} />
                        </div>
                    ),
            },
            {
                id: FILTER_SPALTEN.betreff,
                header: "Art & Betreff",
                pinned: true,
                filter: { kind: "text" },
                minWidth: 200,
                className: "kt-wrap",
                cell: (row) => {
                    const key = `${row.kind}-${row.id}`;
                    const mitKindern = !row.isChild && row.kind === "vorgang" && (row.children?.length ?? 0) > 0;
                    const auf = offen.has(key);
                    return (
                        <div style={{ display: "flex", alignItems: "flex-start", gap: 6 }}>
                            {mitKindern ? (
                                <span
                                    role="button"
                                    aria-label={auf ? "Zuklappen" : "Aufklappen"}
                                    title={auf ? "Zuklappen" : "Aufklappen"}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        umschalten(key);
                                    }}
                                    style={{
                                        cursor: "pointer",
                                        color: REFINED.aub,
                                        width: 22,
                                        height: 22,
                                        flexShrink: 0,
                                        display: "inline-flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        borderRadius: 6,
                                        lineHeight: 1,
                                        marginTop: 1,
                                        backgroundColor: REFINED.aub50,
                                    }}
                                >
                                    <span
                                        style={{
                                            display: "inline-flex",
                                            transform: auf ? "rotate(0deg)" : "rotate(-90deg)",
                                            transition: "transform 0.16s ease",
                                        }}
                                    >
                                        {I.chevD}
                                    </span>
                                </span>
                            ) : (
                                <span style={{ width: row.isChild ? 24 : 22, flexShrink: 0, display: "inline-block" }} />
                            )}
                            <span
                                style={{
                                    whiteSpace: "normal",
                                    overflowWrap: "anywhere",
                                    wordBreak: "break-word",
                                    fontWeight: row.isChild ? 400 : 600,
                                    color: row.isChild ? REFINED.ink2 : REFINED.ink,
                                }}
                            >
                                {zeigtArtChip(row.vorgangsart) && (
                                    <span
                                        style={{
                                            display: "inline-block",
                                            marginRight: 6,
                                            padding: "1px 7px",
                                            borderRadius: 999,
                                            backgroundColor: REFINED.aub50,
                                            color: REFINED.aub,
                                            fontSize: "0.72rem",
                                            fontWeight: 800,
                                            letterSpacing: "0.04em",
                                            verticalAlign: "1px",
                                        }}
                                    >
                                        {(row.vorgangsart as string).trim()}
                                    </span>
                                )}
                                {betreffOhneArt(row.vorgangsart, row.titel) || (
                                    <span style={{ color: REFINED.ink2, fontWeight: 400 }}>
                                        {zeigtArtChip(row.vorgangsart) ? "" : "Ohne Betreff"}
                                    </span>
                                )}
                            </span>
                        </div>
                    );
                },
            },
            // „K-Verantwortlich" fehlt hier bewusst (PO 08.09.2026): in einer
            // Kontaktakte ist die Betreuung des Kontakts für jede Zeile dieselbe
            // und steht bereits im Kopf der Akte.
            {
                id: FILTER_SPALTEN.beteiligte,
                header: "Beteiligte",
                hideable: true,
                collapsible: true,
                filter: { kind: "people", options: personenOptionen },
                minWidth: 120,
                cell: (row) => <PeopleCell groups={row.beteiligte} />,
            },
            // TWV vor der Frist — wie in der Vorgangstabelle: die Wiedervorlage
            // sagt, wann man sich das Thema wieder vornimmt.
            {
                id: FILTER_SPALTEN.twv,
                header: "TWV",
                hideable: true,
                collapsible: true,
                filter: { kind: "dateRange" },
                minWidth: 120,
                cell: (row) => <DateCell iso={row.twv} variant="twv" />,
            },
            {
                id: FILTER_SPALTEN.frist,
                header: "Frist",
                hideable: true,
                collapsible: true,
                filter: { kind: "dateRange" },
                minWidth: 120,
                cell: (row) => <DateCell iso={row.frist} overdue={isOverdueFrist(row)} />,
            },
            {
                id: FILTER_SPALTEN.ersteller,
                header: "Ersteller",
                hideable: true,
                collapsible: true,
                filter: { kind: "people", options: personenOptionen },
                minWidth: 130,
                cell: (row) =>
                    row.ersteller ? (
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 7, minWidth: 0 }}>
                            <InitialsAvatar
                                name={row.ersteller.name}
                                size={24}
                                userId={row.ersteller.userId}
                                label={row.ersteller.initials || undefined}
                            />
                            <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 13 }}>
                                {row.ersteller.name}
                            </span>
                        </span>
                    ) : null,
            },
            {
                id: FILTER_SPALTEN.erstellt,
                header: "Erstellt",
                hideable: true,
                collapsible: true,
                filter: { kind: "dateRange" },
                minWidth: 110,
                cell: (row) => <DateCell iso={row.createdAt} variant="erstellt" />,
            },
        ],
        [offen, personenOptionen],
    );

    return (
        <DataTable
            columns={columns}
            rows={rows}
            rowKey={(r) => `${r.kind}-${r.id}`}
            // Eine To-do-Zeile hat keine eigene Detailseite — sie öffnet ihren
            // Vorgang, und dort klappt sie gleich auf (User 01.11.2026).
            onRowClick={(r) =>
                navigate(
                    r.kind === "todo" && r.vorgangId != null
                        ? vorgangSprungLink(r.vorgangId, { todoId: r.id })
                        : `/vorgaenge/${r.kind === "todo" ? r.vorgangId : r.id}`,
                )
            }
            rowChildren={(r) => (r.kind === "vorgang" ? r.children ?? [] : [])}
            expandedKeys={offen}
            columnVisibility={VERSTECKT}
            filterValues={filter.text}
            onFilterChange={onFilterChange}
            dateRangeValues={filter.datum}
            onDateRangeChange={onDatumChange}
            peopleValues={filter.personen}
            onPeopleChange={onPersonenChange}
            collapsedColumns={eingeklappt}
            onToggleCollapsed={onToggleSpalte}
            tableClassName="kt-dense"
            emptyText={leerText}
        />
    );
};
